<template>
  <BaseWidget 
    :class="['cashflow-chart-widget', widget.type]" 
    :loading="loading" 
    :widget-options="widgetMenuOptions"
    @refresh-data="refreshData"
    @set-config="setConfig"
    v-on="$listeners">
    <template v-slot:header>
      <div class="title">
        <span class="title-icon">
        </span>
        {{ title }}
      </div>
      <div class="description">{{ subtitle }} <span class="text-lowercase">{{ !config.months ? config.year : monthNames[config.months]
      }}, {{ periodNames[config.period.days] }}</span></div>
    </template>
    <template v-slot:content>
      <div v-if="!loading && emptyChartData">
        <EmptyState :content-center="true">
          <template v-slot:image>
            <img src="/static/img/empty-states/mock-chart.png">
          </template>
          <template v-slot:title>
            {{ $gettext('We couldn\'t find any item for this period') }}
          </template>
          <template v-slot:content>
            <span v-if="showCreate">{{ $gettext('Start by creating a new invoice or cost to see your revenue and expenses chart.') }}</span>
          </template>
          <template v-slot:cta>
            <GekkoButton 
              v-if="showCreate"
              :text="$gettext('New invoice')"
              :href="newInvoiceUrl"
              icon="fa-solid fa-plus"
              class="mr-2" />
            <GekkoButton 
              v-if="showCreate"
              :text="$gettext('New cost')"
              :to="newCostUrl"
              icon="fa-solid fa-plus"
              class="mr-2" />
          </template>
        </EmptyState>
      </div>
      <CashflowChart v-if="!loading && !emptyChartData" :data="chartData" :options="chartOptions" />
    </template>
  </BaseWidget>
</template>

<script>
import CashflowChart from '@/components/dashboard/charts/Cashflow'
import BaseWidget from "@/components/dashboard/widgets/BaseWidget"
import DashboardService from "@/store/services/dashboard.service.js";
import EmptyState from "@/components/ui/EmptyState"
import GekkoButton from "@/components/GekkoButton";
import { mapGetters } from "vuex";
import moment from 'moment';

export default {
  name: 'cashflow-widget',
  components: { BaseWidget, CashflowChart, EmptyState, GekkoButton },
  props: {
    widget: {
      type: Object,
      default: () => { }
    },
  },
  data() {
    return {
      loading: true,
      showCreate: true,
      title: '',
      subtitle: '',
      monthName: '',
      disableQuarterly: false,
      disableMonthsBack: false,
      defaultPeriod: 30,
      defaultYear: new Date().getFullYear(),
      defaultMonths: 0,
      config: { months: this.defaultMonths, year: this.defaultYear, period: { days: this.defaultPeriod } },
      emptyChartData: false,
      periodNames: {
        1: this.$gettext('Daily'),
        30: this.$gettext('Monthly'),
        90: this.$gettext('Quarterly')
      },
      monthNames: {
        1: this.$gettext('1 Month back'),
        3: this.$gettext('3 Months back'),
        12: this.$gettext('12 Months back')
      },
      financiallyActiveYears: [],
      filters: {
        start_date: '',
        end_date: '',
        months_back: 0
      },
      invoicesTotals: [],
      costsTotals: [],
      chartData: null,
      chartOptions: {
        title: { display: false },
        scales: {
          xAxes: [{
            time: {
              displayFormats: {
                quarter: ' YYYY'
              }
            },
            stacked: true,
            beginAtZero: true,
            barPercentage: 0.9,
            id: 'x-axis-label',
            position: 'bottom',
            scaleStartValue: 50,
            gridLines: {
              display: false
            },
          }],
          yAxes: [{
            stacked: true,
            id: 'y-axis-label',
            ticks: {
              beginAtZero: true,
              callback: (value) => {
                return '€ ' + this.$options.filters.formatNumberInt(value)
              }
            },
            position: 'left',
            gridLines: {
              color: "#f9f9f9",
              display: true,
              drawBorder: false,
              zeroLineColor: "#e5e5e5",
              zeroLineWidth: 1
            },
          }]
        },
        legend: {
          display: false,
          position: 'bottom'
        },
        maintainAspectRatio: false,
        scaleBeginAtZero: true,
        tooltips: {
          callbacks: {
            title: (tooltipItem, data) => {
              return data['labels'][tooltipItem[0]['index']] + ' ' + data['datasets'][tooltipItem[0]['datasetIndex']]['label'];
            },
            label: (tooltipItem) => {
              return '€ ' + this.$options.filters.formatNumberInt(tooltipItem.value)
            }
          },
          backgroundColor: '#22AC8A', // $colorBrand
          titleFontSize: 14,
          cornerRadius: 8,
          titleFontColor: '#ffffff',
          bodyFontColor: '#ffffff',
          bodyFontSize: 14,
          displayColors: false
        }
      }
    }
  },
  computed: {
    widgetMenuOptions() {
      if (this.loading) return []
      return ([
        { name: this.periodNames[1], event: { name: 'set-config', config: { period: { days: 1 } } } },
        { name: this.periodNames[30], event: { name: 'set-config', config: { period: { days: 30 } } } },
        { name: this.periodNames[90], event: { name: 'set-config', config: { period: { days: 90 } } }, disabled: this.disableQuarterly },
        { separator: true },
        {
          name: this.$gettext('Interval'), submenu: true,
          items: [
            { name: this.monthNames[1], event: { name: 'set-config', config: { months: 1 } }, disabled: this.disableMonthsBack },
            { name: this.monthNames[3], event: { name: 'set-config', config: { months: 3 } }, disabled: this.disableMonthsBack },
            { name: this.monthNames[12], event: { name: 'set-config', config: { months: 12 } }, disabled: this.disableMonthsBack },
            ...this.$store.getters['dashboard/getFinanciallyActiveYears']
          ]
        }
      ])
    },
    newInvoiceUrl() {
      return window.Urls['invoices_add_invoice_step1']()
    },
    newCostUrl() {
      return window.Urls['costs_add_cost']()
    },
    ...mapGetters({
      currentRights: "main/getCurrentRights",
      getCompanyId: 'main/getCompanyId'
    })
  },
  mounted() {
    this.setupWidget()
    this.setInitialConfig(this.widget.config)
    if (this.currentRights['costs'] && this.currentRights['invoices']) {
      this.setPeriodFilters()
      this.refreshData()
    } else {
      this.emptyChartData = true;
      this.loading = false;
      this.showCreate = false;
    }
  },
  methods: {
    setInitialConfig(config) {
      if (config.period) {
        this.selectPeriod(config)
      }
      if (config.months) {
        this.selectMonths(config)
      }
      else {
        if (config.year) {
          this.selectYear(config)
        }
      }
    },
    setConfig(config) {
      if (config.period) {
        this.selectPeriod(config)
      }
      if (config.months) {
        this.selectMonths(config)
      }
      else {
        if (config.year) {
          this.selectYear(config)
        }
      }
      this.setPeriodFilters()
      this.refreshData()

      this.$emit('save-config', this.config)
    },
    selectPeriod(config) {
      const days = (config.period?.days) ? config.period.days : this.defaultPeriod
      this.setupPeriod(days)
    },
    selectYear(config) {
      this.config.year = (config.year) ? config.year : this.defaultYear
      this.config.months = this.defaultMonths
      this.filters.months_back = this.defaultMonths
    },
    selectMonths(config) {
      const months = (config.months) ? config.months : this.defaultMonths
      this.config.months = months
      this.filters.months_back = months
    },
    refreshData() {
      if (this.currentRights['costs'] && this.currentRights['invoices']) {
        this.getData()
      }
    },
    setupPeriod(days) {
      this.config.period.days = days
      if (days === 1) this.filters.group_by = 'day'
      if (days === 30) this.filters.group_by = 'month'
      if (days === 90) this.filters.group_by = 'quarter'
    },
    setPeriodFilters() {
      if (!this.config.year) this.config.year = this.defaultYear
      if (this.filters.months_back) {
        var d = new Date();
        this.filters.end_date = moment(d).format('YYYY-MM-DD');
        this.filters.start_date = moment(d).month(moment(d).month() - this.filters.months_back).add(1, 'days').format('YYYY-MM-DD');
      }
      else {
        this.filters.end_date = this.config.year + '-12-31';
        this.filters.start_date = this.config.year + '-01-01';
      }
    },
    getData() {
      this.loading = true
      this.getInvoicesTotals('s').then(() => {
        this.getInvoicesTotals('p').then(() => {
          this.getCostsTotals('notpaid').then(() => {
            this.getCostsTotals().then(() => {

              this.emptyChartData = (
                this.invoicesTotals['p'].every(item => item === 0)
                && this.invoicesTotals['s'].every(item => item === 0)
                && this.costsTotals['notpaid'].every(item => item === 0)
                && this.costsTotals['total'].every(item => item === 0)
              )

              if (!this.emptyChartData) {
                this.formatChartData()
              }

              this.loading = false
            })
          })
        })
      })
    },
    setupDailyAmounts(start, end) {
      //Populate a map with n days for daily visualisation (key: dates, value: sums)
      const days = new Map();
      let startDate = new Date(start);
      let endDate = new Date(end);
      let amount = Math.ceil(Math.abs(endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      let date = startDate;
      for (let day = 0; day < amount; day++) {
        date.setDate(date.getDate() + (day ? 1 : 0));
        days.set(date.toISOString().split('T')[0], 0.0);
      }
      return days;
    },
    mapDataToDailyAmounts(response){
      let map = this.setupDailyAmounts(this.filters.start_date, this.filters.end_date);
      //Get values from the backend and replace daily visualisation empty values (sums) where the keys (dates) match
      for (let i = 0; i < response.data.length; i++) {
        map.set(Object.values(response.data[i])[0], response.data[i].total);
      }
      //Finally, extract the values (sums) from the map and set the result array to that
      return Array.from(map.values());
    },
    getInvoicesTotals(status) {
      const local_filter = { status: status, sender_company: this.getCompanyId }
      return DashboardService.invoicesTotalNet({ ...this.filters, ...local_filter }).then((response) => {
        this.filters.group_by === 'day' ? this.invoicesTotals[status] = this.mapDataToDailyAmounts(response) : this.invoicesTotals[status] = response.data;
      })
    },
    getCostsTotals(payment = false) {
      const local_filter = { owner_company: this.getCompanyId, test: "company_owner should be here", test2: this.$router.currentRoute.fullPath }
      if (payment) local_filter.payment_type = payment
      return DashboardService.costsTotalNet({ ...this.filters, ...local_filter }).then((response) => {
        this.filters.group_by === 'day' ? this.costsTotals[(payment) ? payment : 'total'] = this.mapDataToDailyAmounts(response) : this.costsTotals[(payment) ? payment : 'total'] = response.data;
      })
    },
    //Dynamic array of month labels in order, from a start date to an end date
    getMonthsRange(start, end) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      let startDate = new Date(start);
      let endDate = new Date(end);
      let diffYear = (12 * (endDate.getFullYear() - startDate.getFullYear())) + endDate.getMonth();
      let result = [];
      for (var i = startDate.getMonth(); i <= diffYear; i++) {
        result.push(months[i % 12]);
      }
      return result;
    },
    formatChartData() {
      //The labels and data have to match for the chart to work, so set it accordingly to the selected option
      let values = [];
      switch (this.filters.group_by) {
        case 'quarter':
          values = ['Q1', 'Q2', 'Q3', 'Q4'];
          break;
        case 'month':
          values = this.getMonthsRange(this.filters.start_date, this.filters.end_date);
          break;
        case 'day': {
          let amounts = this.setupDailyAmounts(this.filters.start_date, this.filters.end_date);
          values = Array.from(amounts.keys());
          break;
        }
      }
      const labels = values;

      const incomeSum = this.invoicesTotals['s'].map((num, idx) => {
        return num + this.invoicesTotals['p'][idx];
      });

      this.costsTotals['paid'] = this.costsTotals['total'].map((item, index) => item - this.costsTotals['notpaid'][index])

      const average = incomeSum.map((num, idx) => {
        return num - this.costsTotals['total'][idx];
      });

      const chartData = {
        labels: labels,
        datasets: [
          {
            type: 'line',
            label: this.$gettext('Margin'),
            data: average,
            borderColor: '#37a0cd', // $colorBlue
            fill: false,
            lineTension: 0,
            borderJoinStyle: 'miter',
            xAxes: [{ barPercentage: 0.4 }]
          },
          {
            type: 'bar',
            label: this.$gettext('Revenue (paid)'),
            backgroundColor: "#22ac8A", // $colorBrand
            data: this.invoicesTotals['p'],
            stack: 'Stack income'
          },
          {
            type: 'bar',
            label: this.$gettext('Revenue (not paid)'),
            backgroundColor: "#64c5ad",
            data: this.invoicesTotals['s'],
            stack: 'Stack income'
          },
          {
            type: 'bar',
            label: this.$gettext('Expenses (paid)'),
            backgroundColor: "#ac8255", // $colorBrown
            data: this.costsTotals['paid'],
            stack: 'Stack expenses'
          },
          {
            type: 'bar',
            label: this.$gettext('Expenses (not paid)'),
            backgroundColor: "#c5a788",
            data: this.costsTotals['notpaid'],
            stack: 'Stack expenses'
          }
        ]
      }
      this.chartData = chartData

      // Disable menu options that do not make sense, for example: 1 month back, quarterly
      if (this.config.period.days == 90) {
        this.disableMonthsBack = true
      }
      else {
        this.disableMonthsBack = false
      }
      if (this.config.months) {
        this.disableQuarterly = true
      }
      else {
        this.disableQuarterly = false
      }
    },
    setupWidget() {
      this.title = this.$gettext('Revenue and expenses')
      this.subtitle = this.$gettext('Data from')
    }
  }
}
</script>

<style lang="scss">
.cashflow-chart-widget {
  .widget-content {
    width: 100% !important;
    display: block !important;
  }
}
</style>
<style lang="scss" scoped>
.cashflow-chart-widget {
  .widget-content {
    width: 100% !important;
    display: block !important;

    >div {
      height: 100%;
    }

    .chartjs-size-monitor {
      height: 100%;
    }

    .chart {
      display: flex;
      align-items: center;
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
